<template>
  <div style="padding: 16px; background: #F8F8F9; display: flex; flex-direction: column; gap: 16px;">
    <!-- Loading -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <template v-else>
      <!-- Eligibility Rules -->
      <div class="cm-panel">
        <div class="cm-panel-head">
          <div class="cm-panel-title">Eligibility Rules</div>
          <div class="cm-panel-head-actions">
            <button class="cm-btn cm-btn-primary cm-btn-sm" @click="openRuleModal('add')">
              <FontAwesomeIcon :icon="['fas', 'plus']" style="font-size: 10px;" /> Add Rule
            </button>
          </div>
        </div>
        <table class="cm-table">
          <thead>
            <tr>
              <th>Program</th>
              <th>Required Tags</th>
              <th>Allowed Contracts</th>
              <th class="num" style="width: 80px;">Practices</th>
              <th style="width: 60px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rule, idx) in eligibilityRules" :key="idx">
              <td><span class="strong">{{ rule.metricName }}</span></td>
              <td>
                <span style="display: flex; gap: 4px; flex-wrap: wrap;">
                  <span v-for="tag in rule.requiredTags" :key="tag" class="cm-badge cm-badge-sq info">{{ tag }}</span>
                </span>
              </td>
              <td>
                <span v-if="!rule.contracts" style="color: #A5A5A6;">Any</span>
                <span v-else style="display: flex; gap: 4px; flex-wrap: wrap;">
                  <span v-for="c in rule.contracts" :key="c" class="cm-badge cm-badge-sq">{{ c }}</span>
                </span>
              </td>
              <td class="num"><span class="strong cm-tnum">{{ rule.matchingPractices }}</span></td>
              <td>
                <span style="display: flex; gap: 4px;">
                  <button class="cm-icon-btn" @click="openRuleModal('edit', rule, idx)" title="Edit"><FontAwesomeIcon :icon="['fas', 'pen']" style="font-size: 10px;" /></button>
                  <button class="cm-icon-btn" @click="confirmDeleteRule(rule, idx)" title="Delete" style="color: #A5A5A6;"><FontAwesomeIcon :icon="['fas', 'trash-can']" style="font-size: 10px;" /></button>
                </span>
              </td>
            </tr>
            <tr v-if="eligibilityRules.length === 0">
              <td colspan="5" style="text-align: center; color: #A5A5A6; padding: 16px;">No eligibility rules configured</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Practice Assignments matrix -->
      <div class="cm-panel">
        <div class="cm-panel-head">
          <div class="cm-panel-title">Practice Assignments<span class="sub">{{ practices.length }} practices</span></div>
          <div class="cm-panel-head-actions">
            <input class="cm-input cm-input-sm" placeholder="Search practices…" v-model="searchQuery" style="width: 200px;" />
          </div>
        </div>
        <div>
          <table class="cm-table" style="table-layout: fixed;">
            <colgroup>
              <col style="width: 200px;" />
              <col v-for="tag in cmTags" :key="tag.id" style="width: 64px;" />
            </colgroup>
            <thead>
              <tr>
                <th style="width: 200px;">Practice</th>
                <th v-for="tag in cmTags" :key="tag.id" class="ctr" style="width: 64px;">
                  <div style="text-overflow: ellipsis;" :title="tag.name">{{ tagShortName(tag.name) }}</div>
                  <div style="font-weight: 400; color: #A5A5A6;">{{ tagCounts[tag.id] || 0 }}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="practice in filteredPractices" :key="practice.id">
                <td style="text-overflow: ellipsis;" :title="practice.name"><span class="strong">{{ practice.name }}</span></td>
                <td v-for="tag in cmTags" :key="tag.id" class="ctr">
                  <button
                    @click="hasTag(practice.id, tag.id) ? confirmRemove(practice, tag) : confirmAdd(practice, tag)"
                    :disabled="saving[practice.id + '-' + tag.id]"
                    :title="hasTag(practice.id, tag.id) ? 'Click to remove' : 'Click to add'"
                    style="width: 24px; height: 24px; border-radius: 50%; border: none; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: opacity 0.12s, background 0.12s;"
                    :style="{
                      background: hasTag(practice.id, tag.id) ? '#E9F7F0' : 'transparent',
                      color: hasTag(practice.id, tag.id) ? '#0E6B43' : '#A5A5A6',
                      opacity: hasTag(practice.id, tag.id) ? 1 : 0.35,
                    }"
                    @mouseenter="$event.currentTarget.style.opacity = 1"
                    @mouseleave="$event.currentTarget.style.opacity = hasTag(practice.id, tag.id) ? 1 : 0.35"
                  >
                    <FontAwesomeIcon :icon="['fas', hasTag(practice.id, tag.id) ? 'check' : 'plus']" style="font-size: 11px;" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Rule add/edit modal -->
    <Teleport to="body">
      <div v-if="ruleModal" class="cm-overlay" @click.self="ruleModal = null">
        <div class="cm-modal" style="max-width: 480px;">
          <div class="cm-modal-head">
            <h3>{{ ruleModal.mode === 'add' ? 'Add Eligibility Rule' : 'Edit Eligibility Rule' }}</h3>
            <button class="cm-icon-btn" @click="ruleModal = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <div>
              <label style="display: block; font-size: 11.5px; font-weight: 500; color: #626264; margin-bottom: 5px;">Program</label>
              <select v-model="ruleModal.metricId" :disabled="ruleModal.mode === 'edit'" class="cm-select" style="width: 100%;">
                <option :value="null" disabled>Select program…</option>
                <option v-for="m in metrics" :key="m.id" :value="m.id">{{ m.name }}</option>
              </select>
            </div>
            <div>
              <label style="display: block; font-size: 11.5px; font-weight: 500; color: #626264; margin-bottom: 5px;">Required Tags</label>
              <div style="display: flex; flex-wrap: wrap; gap: 8px; border: 1px solid #E8E9E9; border-radius: 6px; padding: 10px;">
                <label v-for="tag in cmTags" :key="tag.id" style="display: flex; align-items: center; gap: 4px; font-size: 11.5px; cursor: pointer;">
                  <input type="checkbox" :value="tag.name" v-model="ruleModal.requiredTags" /> {{ tagShortName(tag.name) }}
                </label>
              </div>
            </div>
            <div>
              <label style="display: block; font-size: 11.5px; font-weight: 500; color: #626264; margin-bottom: 5px;">Allowed Contract IDs <span style="color: #A5A5A6; font-weight: 400;">(comma-separated, blank = any)</span></label>
              <input v-model="ruleModal.contractsText" class="cm-input" style="width: 100%;" placeholder="e.g. A4592, D0171" />
            </div>
          </div>
          <div class="cm-modal-foot">
            <button class="cm-btn cm-btn-secondary" @click="ruleModal = null">Cancel</button>
            <button class="cm-btn cm-btn-primary" @click="saveRule" :disabled="!ruleModal.metricId || ruleModal.requiredTags.length === 0 || savingRule">
              {{ savingRule ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete rule confirmation -->
    <Teleport to="body">
      <div v-if="deleteRuleConfirm" class="cm-overlay" @click.self="deleteRuleConfirm = null">
        <div class="cm-modal" style="max-width: 400px;">
          <div class="cm-modal-head">
            <h3>Delete Rule</h3>
            <button class="cm-icon-btn" @click="deleteRuleConfirm = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <div style="font-size: 12.5px; color: #353537;">
              Delete this eligibility rule from <strong>{{ deleteRuleConfirm.metricName }}</strong>?
            </div>
            <div style="font-size: 11.5px; color: #8B8B8D;">Tags: {{ deleteRuleConfirm.requiredTags.join(', ') }}</div>
          </div>
          <div class="cm-modal-foot">
            <button class="cm-btn cm-btn-secondary" @click="deleteRuleConfirm = null">Cancel</button>
            <button class="cm-btn cm-btn-danger" @click="executeDeleteRule" :disabled="savingRule">Delete</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Tag add/remove confirmation modal -->
    <Teleport to="body">
      <div v-if="confirmAction" class="cm-overlay" @click.self="confirmAction = null">
        <div class="cm-modal" style="max-width: 400px;">
          <div class="cm-modal-head">
            <h3>{{ confirmAction.type === 'add' ? 'Add Tag' : 'Remove Tag' }}</h3>
            <button class="cm-icon-btn" @click="confirmAction = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <div style="font-size: 12.5px; color: #353537;">
              {{ confirmAction.type === 'add' ? 'Add' : 'Remove' }}
              <strong>{{ confirmAction.tagName }}</strong>
              {{ confirmAction.type === 'add' ? 'to' : 'from' }}
              <strong>{{ confirmAction.practiceName }}</strong>?
            </div>
          </div>
          <div class="cm-modal-foot">
            <button class="cm-btn cm-btn-secondary" @click="confirmAction = null">Cancel</button>
            <button class="cm-btn" :class="confirmAction.type === 'add' ? 'cm-btn-primary' : 'cm-btn-danger'"
              @click="executeConfirmedAction" :disabled="saving[confirmAction.practiceId + '-' + confirmAction.tagId]">
              {{ confirmAction.type === 'add' ? 'Add' : 'Remove' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { listCareManagementPrograms } from '@/services/api/careManagementPrograms';

const CM_TAG_NAMES = [
  'Vytal Care - TOC',
  'Vytal Care- TOC Low LACE',
  'Vytal Care- TOC ED',
  'Vytal Care- TOC ED Low',
  'Vytal Care TOC- Private Payer',
  'Vytal Care+',
  'Vytal Care+ TOC',
  'CDM',
];

const TAG_SHORT = {
  'Vytal Care - TOC': 'TOC',
  'Vytal Care- TOC Low LACE': 'Low LACE',
  'Vytal Care- TOC ED': 'TOC ED',
  'Vytal Care- TOC ED Low': 'ED Low',
  'Vytal Care TOC- Private Payer': 'PP',
  'Vytal Care+': 'VC+',
  'Vytal Care+ TOC': 'VC+ TOC',
  'CDM': 'CDM',
};

export default {
  name: 'ProgramAssignments',
  components: { FontAwesomeIcon },
  data() {
    return {
      loading: false,
      cmTags: [],
      practices: [],
      // { 'practiceId-tagId': relationshipId }
      tagMap: {},
      searchQuery: '',
      saving: {},
      hoverRow: null,
      metrics: [],
      confirmAction: null, // { type: 'add'|'remove', practiceId, tagId, practiceName, tagName }
      ruleModal: null, // { mode: 'add'|'edit', metricId, requiredTags: [], contractsText: '', ruleIndex }
      deleteRuleConfirm: null, // { metricId, metricName, ruleIndex, requiredTags }
      savingRule: false,
    };
  },
  computed: {
    // Flattened list of eligibility rules across all CM metrics
    eligibilityRules() {
      const rows = [];
      const tagNameToId = {};
      for (const t of this.cmTags) tagNameToId[t.name] = t.id;

      for (const m of this.metrics) {
        const oc = m.opportunity_config || {};
        const rules = oc.eligibility_rules || [];
        for (let ruleIdx = 0; ruleIdx < rules.length; ruleIdx++) {
          const rule = rules[ruleIdx];
          const tags = rule.required_tags || [];
          // Count practices that have ALL required tags
          let matchCount = 0;
          if (tags.length > 0) {
            const tagIds = tags.map(t => tagNameToId[t]).filter(Boolean);
            if (tagIds.length === tags.length) {
              matchCount = this.practices.filter(p =>
                tagIds.every(tid => this.tagMap[p.id + '-' + tid])
              ).length;
            }
          }
          rows.push({
            metricId: m.id,
            metricName: m.name,
            metricSlug: m.metric_name_slug,
            ruleIndex: ruleIdx,
            requiredTags: tags,
            contracts: rule.allowed_contract_ids || null,
            matchingPractices: matchCount,
          });
        }
        if (rules.length === 0) {
          rows.push({
            metricId: m.id,
            metricName: m.name,
            metricSlug: m.metric_name_slug,
            ruleIndex: null,
            requiredTags: [],
            contracts: null,
            matchingPractices: this.practices.length,
          });
        }
      }
      return rows;
    },
    filteredPractices() {
      if (!this.searchQuery) return this.practices;
      const q = this.searchQuery.toLowerCase();
      return this.practices.filter(p => p.name.toLowerCase().includes(q));
    },
    tagCounts() {
      const counts = {};
      for (const tag of this.cmTags) {
        counts[tag.id] = 0;
      }
      for (const key of Object.keys(this.tagMap)) {
        const tagId = parseInt(key.split('-')[1]);
        if (counts[tagId] !== undefined) counts[tagId]++;
      }
      return counts;
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    tagShortName(name) {
      return TAG_SHORT[name] || name;
    },

    hasTag(practiceId, tagId) {
      return !!this.tagMap[practiceId + '-' + tagId];
    },

    async loadData() {
      this.loading = true;
      try {
        // Load tags
        const tagsResp = await axios.get('/api/infotags/');
        const allTags = tagsResp.data.results || tagsResp.data;
        this.cmTags = allTags
          .filter(t => CM_TAG_NAMES.includes(t.name))
          .sort((a, b) => CM_TAG_NAMES.indexOf(a.name) - CM_TAG_NAMES.indexOf(b.name));

        // Load practices, metrics, and relationships in parallel
        const [practicesResp, metrics, ...relResps] = await Promise.all([
          axios.get('/api/entities/', { params: { entity_type_name: 'Practice', page_size: 5000 } }),
          listCareManagementPrograms(),
          ...this.cmTags.map(t => axios.get('/api/infoTagRelationships/', {
            params: { infoTag_name: t.name, page_size: 5000 }
          })),
        ]);

        this.metrics = metrics;

        this.practices = (practicesResp.data.results || practicesResp.data)
          .map(p => ({ id: p.id, name: p.name }))
          .sort((a, b) => a.name.localeCompare(b.name));

        // Build tag map: { 'practiceId-tagId': relationshipId }
        this.tagMap = {};
        this.cmTags.forEach((tag, idx) => {
          const rels = relResps[idx].data.results || relResps[idx].data;
          for (const r of rels) {
            this.tagMap[r.entity + '-' + tag.id] = r.id;
          }
        });
      } finally {
        this.loading = false;
      }
    },

    openRuleModal(mode, rule, idx) {
      if (mode === 'add') {
        this.ruleModal = { mode: 'add', metricId: null, requiredTags: [], contractsText: '', ruleIndex: null };
      } else {
        this.ruleModal = {
          mode: 'edit',
          metricId: rule.metricId,
          requiredTags: [...rule.requiredTags],
          contractsText: (rule.contracts || []).join(', '),
          ruleIndex: rule.ruleIndex,
        };
      }
    },

    confirmDeleteRule(rule, idx) {
      this.deleteRuleConfirm = {
        metricId: rule.metricId,
        metricName: rule.metricName,
        ruleIndex: rule.ruleIndex,
        requiredTags: rule.requiredTags,
      };
    },

    async saveRule() {
      this.savingRule = true;
      try {
        const metric = this.metrics.find(m => m.id === this.ruleModal.metricId);
        if (!metric) return;

        const config = JSON.parse(JSON.stringify(metric.opportunity_config || {}));
        if (!config.eligibility_rules) config.eligibility_rules = [];

        const newRule = { required_tags: this.ruleModal.requiredTags };
        const contracts = this.ruleModal.contractsText.split(',').map(s => s.trim()).filter(Boolean);
        if (contracts.length) newRule.allowed_contract_ids = contracts;

        if (this.ruleModal.mode === 'add') {
          config.eligibility_rules.push(newRule);
        } else {
          config.eligibility_rules[this.ruleModal.ruleIndex] = newRule;
        }

        await axios.patch(`/api/metrics/${metric.id}/`, { opportunity_config: config });
        metric.opportunity_config = config;
        this.ruleModal = null;
      } catch (e) {
        const detail = e.response?.data?.error || e.response?.data?.detail || e.message;
        this.$root.$emit('show-error-toast', `Failed to save rule: ${detail}`);
      } finally {
        this.savingRule = false;
      }
    },

    async executeDeleteRule() {
      this.savingRule = true;
      try {
        const { metricId, ruleIndex } = this.deleteRuleConfirm;
        const metric = this.metrics.find(m => m.id === metricId);
        if (!metric) return;

        const config = JSON.parse(JSON.stringify(metric.opportunity_config || {}));
        config.eligibility_rules.splice(ruleIndex, 1);

        await axios.patch(`/api/metrics/${metric.id}/`, { opportunity_config: config });
        metric.opportunity_config = config;
        this.deleteRuleConfirm = null;
      } catch (e) {
        const detail = e.response?.data?.error || e.response?.data?.detail || e.message;
        this.$root.$emit('show-error-toast', `Failed to delete rule: ${detail}`);
      } finally {
        this.savingRule = false;
      }
    },

    confirmAdd(practice, tag) {
      this.confirmAction = { type: 'add', practiceId: practice.id, tagId: tag.id, practiceName: practice.name, tagName: tag.name };
    },

    confirmRemove(practice, tag) {
      this.confirmAction = { type: 'remove', practiceId: practice.id, tagId: tag.id, practiceName: practice.name, tagName: tag.name };
    },

    async executeConfirmedAction() {
      const { type, practiceId, tagId } = this.confirmAction;
      if (type === 'add') {
        await this.addTag(practiceId, tagId);
      } else {
        await this.removeTag(practiceId, tagId);
      }
      this.confirmAction = null;
    },

    async addTag(practiceId, tagId) {
      const key = practiceId + '-' + tagId;
      this.saving[key] = true;
      try {
        const resp = await axios.post('/api/infoTagRelationships/', {
          entity: practiceId,
          infoTag: tagId,
          originating_object_content_type_model: 'entity',
          originating_object_id: practiceId,
        });
        this.tagMap[key] = resp.data.id;
      } catch (e) {
        const detail = e.response?.data?.error || e.response?.data?.detail || e.message;
        this.$root.$emit('show-error-toast', `Failed to add: ${detail}`);
      } finally {
        delete this.saving[key];
      }
    },

    async removeTag(practiceId, tagId) {
      const key = practiceId + '-' + tagId;
      const relId = this.tagMap[key];
      if (!relId) return;
      this.saving[key] = true;
      try {
        await axios.delete(`/api/infoTagRelationships/${relId}/`);
        delete this.tagMap[key];
      } catch (e) {
        const detail = e.response?.data?.error || e.response?.data?.detail || e.message;
        this.$root.$emit('show-error-toast', `Failed to remove: ${detail}`);
      } finally {
        delete this.saving[key];
      }
    },
  },
};
</script>
